import { z } from 'zod';
import { api } from './api';
import { currentUserSchema, type CurrentUser } from '@/types/schemas';
import { createCredential, getAssertion } from '@/utils/webauthn';

/**
 * Настройки → Безопасность, and the passkey login.
 *
 * Both ceremonies are two round trips by design — the server mints a
 * challenge, the device answers it, the server verifies. The `options` blob in
 * the middle is the WebAuthn spec's own shape and is passed to the browser
 * unaltered; nothing in this file interprets it.
 */

const passkeySchema = z.object({
  id: z.string(),
  friendly_name: z.string(),
  created_at: z.string(),
  last_used_at: z.string().nullable().optional().default(null),
  synced: z.boolean().optional().default(false),
});

const passkeyListSchema = z.object({ items: z.array(passkeySchema).default([]) });

/** `options` is unknown on purpose: it belongs to the browser, not to us. */
const passkeyOptionsSchema = z.object({
  challenge_id: z.string(),
  options: z.record(z.unknown()),
});

const passkeyConfigSchema = z.object({ enabled: z.boolean(), rp_id: z.string() });

const loginResponseSchema = z.object({
  user: currentUserSchema,
  csrf_token: z.string(),
});

const sessionSchema = z.object({
  id: z.string(),
  device_label: z.string(),
  auth_method: z.string(),
  created_at: z.string(),
  last_seen_at: z.string(),
  current: z.boolean().optional().default(false),
});

const sessionListSchema = z.object({ items: z.array(sessionSchema).default([]) });
const revokedSchema = z.object({ revoked: z.number() });
const passwordChangeSchema = z.object({ sessions_revoked: z.number() });

export type Passkey = z.infer<typeof passkeySchema>;
export type ActiveSession = z.infer<typeof sessionSchema>;
export type PasskeyConfig = z.infer<typeof passkeyConfigSchema>;

export const securityService = {
  // --- capability ----------------------------------------------------------
  passkeyConfig: (signal?: AbortSignal): Promise<PasskeyConfig> =>
    api.get('/auth/passkey/config', passkeyConfigSchema, signal),

  // --- passkeys ------------------------------------------------------------
  listPasskeys: (signal?: AbortSignal): Promise<Passkey[]> =>
    api
      .get('/auth/passkeys', passkeyListSchema, signal)
      .then((response) => response.items),

  /**
   * Add a passkey to the signed-in account.
   *
   * The whole ceremony, because splitting it across the UI would let a caller
   * do half of it. The browser shows the Face ID / Touch ID prompt during
   * `createCredential`; everything before and after is plain HTTP.
   */
  addPasskey: async (friendlyName?: string): Promise<Passkey> => {
    const { challenge_id, options } = await api.post(
      '/auth/passkey/registration/options',
      passkeyOptionsSchema,
    );
    const credential = await createCredential(options as never);
    return api.post('/auth/passkey/registration/verify', passkeySchema, {
      challenge_id,
      credential,
      friendly_name: friendlyName,
    });
  },

  renamePasskey: (id: string, friendlyName: string): Promise<Passkey> =>
    api.patch(`/auth/passkeys/${id}`, passkeySchema, { friendly_name: friendlyName }),

  deletePasskey: (id: string): Promise<void> => api.delete(`/auth/passkeys/${id}`, z.void()),

  /**
   * Sign in with a passkey. No email is sent, at any point.
   *
   * The credential is discoverable, so the browser's own account picker
   * decides which one to use and the assertion carries an opaque user handle.
   * The server therefore never has to answer «does this address exist?» to
   * anybody who has not already proved they hold the key.
   */
  loginWithPasskey: async (): Promise<CurrentUser> => {
    const { challenge_id, options } = await api.post(
      '/auth/passkey/authentication/options',
      passkeyOptionsSchema,
    );
    const credential = await getAssertion(options as never);
    const response = await api.post('/auth/passkey/authentication/verify', loginResponseSchema, {
      challenge_id,
      credential,
    });
    return response.user;
  },

  // --- sessions ------------------------------------------------------------
  listSessions: (signal?: AbortSignal): Promise<ActiveSession[]> =>
    api
      .get('/auth/sessions', sessionListSchema, signal)
      .then((response) => response.items),

  revokeSession: (id: string): Promise<void> => api.delete(`/auth/sessions/${id}`, z.void()),

  revokeOtherSessions: (): Promise<number> =>
    api
      .post('/auth/sessions/revoke-others', revokedSchema)
      .then((response) => response.revoked),

  // --- password ------------------------------------------------------------
  changePassword: (
    currentPassword: string,
    newPassword: string,
    newPasswordConfirm: string,
  ): Promise<number> =>
    api
      .post('/auth/password', passwordChangeSchema, {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      })
      .then((response) => response.sessions_revoked),
};
